import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "@/pages/HomePage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProfile from "@/pages/AdminProfile";
import ClientLogin from "@/pages/ClientLogin";
import ClientDashboard from "@/pages/ClientDashboard";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import NotFoundPage from "@/pages/NotFoundPage";
import InstallPrompt, { isRunningStandalone } from "@/components/InstallPrompt";
import { Toaster } from "sonner";
import { installAuthInterceptor } from "@/lib/authInterceptor";

installAuthInterceptor();

// Prevent the browser from auto-restoring scroll position on reload/back-forward.
// Runs synchronously during module load, before React mounts, so it beats any
// implicit scroll restoration that happens on first paint.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// SVG favicons - red K on white bg (for dark mode) and red K on black bg (for light mode)
const makeFaviconSvg = (bg) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
<circle cx="32" cy="32" r="32" fill="${bg}"/>
<text x="10" y="46" font-family="Arial,sans-serif" font-weight="900" font-size="42" fill="#E50914">K</text>
<text x="36" y="24" font-family="monospace" font-weight="bold" font-size="14" fill="#E50914">&lt;/&gt;</text>
</svg>`;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Force INSTANT scroll by temporarily overriding the CSS smooth-scroll,
    // then restore. Without this override, `scrollTo(0,0)` animates smoothly
    // on every route change and feels like "page opens mid-scroll".
    const scroll = () => {
      const html = document.documentElement;
      const prev = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo(0, 0);
      // restore after next paint
      requestAnimationFrame(() => { html.style.scrollBehavior = prev; });
    };
    scroll();
    // Second and third passes to defeat late layout shifts
    const t1 = setTimeout(scroll, 50);
    const t2 = setTimeout(scroll, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pathname]);
  return null;
}

function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("koko_token"));

  useEffect(() => {
    // Self-heal: if a previous session left body.no-scroll stuck (e.g., wizard
    // unmounted mid-cleanup), remove it so page scroll is never trapped.
    document.body.classList.remove("no-scroll");

    document.querySelectorAll("link[rel*='icon']").forEach(l => {
      // Preserve the manifest-linked PWA icons (apple-touch-icon etc.)
      if (l.rel === "apple-touch-icon" || l.rel === "manifest") return;
      l.remove();
    });

    const darkLink = document.createElement('link');
    darkLink.rel = 'icon';
    darkLink.type = 'image/svg+xml';
    darkLink.media = '(prefers-color-scheme: dark)';
    darkLink.href = `data:image/svg+xml,${encodeURIComponent(makeFaviconSvg('#FFFFFF'))}`;
    document.head.appendChild(darkLink);

    const lightLink = document.createElement('link');
    lightLink.rel = 'icon';
    lightLink.type = 'image/svg+xml';
    lightLink.media = '(prefers-color-scheme: light)';
    lightLink.href = `data:image/svg+xml,${encodeURIComponent(makeFaviconSvg('#000000'))}`;
    document.head.appendChild(lightLink);

    const fallback = document.createElement('link');
    fallback.rel = 'icon';
    fallback.type = 'image/svg+xml';
    fallback.href = `data:image/svg+xml,${encodeURIComponent(makeFaviconSvg('#FFFFFF'))}`;
    document.head.appendChild(fallback);

    const badge = document.getElementById('emergent-badge');
    if (badge) badge.remove();

    // Keep authed state in sync so InstallPrompt shows after login, hides after logout
    const syncAuth = () => setAuthed(!!localStorage.getItem("koko_token"));
    window.addEventListener("storage", syncAuth);
    window.addEventListener("focus", syncAuth);
    const interval = setInterval(syncAuth, 2000);
    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener("focus", syncAuth);
      clearInterval(interval);
    };
  }, []);

  // PWA standalone mode: if user has a token, always open to their portal
  const standalone = isRunningStandalone();
  let storedRole = null;
  try {
    const u = localStorage.getItem("koko_user");
    if (u) storedRole = JSON.parse(u)?.role || null;
  } catch (_) { /* ignore */ }
  const authedDashboardPath =
    storedRole === "admin" ? "/admin"
    : storedRole === "client" ? "/dashboard"
    : storedRole ? "/staff"
    : "/admin"; // fallback — token without stored user → bootstrap will redirect
  const HomeOrDashboard = standalone && authed ? <Navigate to={authedDashboardPath} replace /> : <HomePage />;

  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={HomeOrDashboard} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/profile" element={<AdminProfile />} />
        <Route path="/staff/login" element={<AdminLogin portal="staff" />} />
        <Route path="/staff" element={<AdminDashboard portal="staff" />} />
        <Route path="/staff/profile" element={<AdminProfile portal="staff" />} />
        <Route path="/login" element={<ClientLogin />} />
        <Route path="/signup" element={<ClientLogin />} />
        <Route path="/dashboard" element={<ClientDashboard />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!standalone && (
        <a
          href="https://wa.me/919242478118?text=Hi%20Kamran!%20I%20visited%20KoKo%20Sites%20and%20I'm%20interested%20in%20your%20web%20design%20services."
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
          data-testid="whatsapp-float-btn"
          aria-label="Chat on WhatsApp"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
      )}
      <InstallPrompt authed={authed} />
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#141414",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 0,
            color: "#fff",
            fontSize: "13px",
          },
        }}
      />
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
